(function () {
  'use strict';

  if (window.OhCrispStickerFallLoaded) return;
  window.OhCrispStickerFallLoaded = true;

  var instances = new WeakMap();
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function random(min, max) {
    return min + Math.random() * (max - min);
  }

  function viewportTier(width) {
    if (width <= 640) return 'mobile';
    if (width <= 900) return 'tablet';
    if (width >= 1200) return 'wide';
    return 'desktop';
  }

  function stickerSizeRange(width) {
    if (width <= 640) {
      return {
        min: clamp(width * 0.075, 26, 34) * 1.15,
        max: clamp(width * 0.125, 40, 52) * 1.15
      };
    }
    if (width <= 900) {
      return {
        min: clamp(width * 0.05, 32, 40) * 1.17,
        max: clamp(width * 0.086, 48, 66) * 1.17
      };
    }
    if (width >= 1200) {
      return {
        min: clamp(width * 0.045, 42, 54) * 0.9375,
        max: clamp(width * 0.072, 66, 88) * 0.9375
      };
    }
    return {
      min: clamp(width * 0.045, 42, 54) * 1.1,
      max: clamp(width * 0.072, 66, 88) * 1.1
    };
  }

  function stickerRadiusRatio(width) {
    var tier = viewportTier(width);
    if (tier === 'wide') return 0.32;
    if (tier === 'tablet') return 0.36;
    return 0.39;
  }

  function kickVisualRock(body, amount, direction) {
    var now = performance.now() / 1000;
    if (now - body.rockStartedAt < 0.12 && amount <= body.rockKick) return;
    body.rockStartedAt = now;
    body.rockKick = clamp(amount, 0.01, 0.085);
    body.rockDirection = direction || (Math.random() < 0.5 ? -1 : 1);
  }

  function loadImage(source) {
    return new Promise(function (resolve) {
      var image = new Image();
      image.decoding = 'async';
      image.onload = function () { resolve(image); };
      image.onerror = function () { resolve(null); };
      image.src = source;
    });
  }

  function StickerFall(section) {
    this.section = section;
    this.canvas = section.querySelector('[data-ohc-sticker-canvas]');
    this.context = this.canvas && this.canvas.getContext('2d');
    this.shakeButton = section.querySelector('[data-ohc-shake]');
    this.bodies = [];
    this.images = [];
    this.obstacles = [];
    this.width = 0;
    this.height = 0;
    this.floorY = 0;
    this.dpr = 1;
    this.viewportTier = '';
    this.gravityX = 0;
    this.gravityTargetX = 0;
    this.gravityStrength = Number(section.dataset.ohcGravity || 1);
    this.chaosMode = section.hasAttribute('data-ohc-spin-chaos');
    this.stickerVersion = section.dataset.ohcStickerVersion || '';
    this.trigger = section.dataset.ohcTrigger || 'on_view';
    this.delay = Math.max(0, Number(section.dataset.ohcDelay || 0));
    this.startedAt = 0;
    this.lastFrame = 0;
    this.lastDrawAt = 0;
    this.lastSettledDrawAt = 0;
    this.raf = 0;
    this.inView = this.trigger === 'page_load';
    this.interactionUntil = 0;
    this.hasStartedFalling = false;
    this.destroyed = false;
    this.dragged = null;
    this.dragPoint = null;
    this.motionEnabled = false;
    this.motionPermissionPending = false;
    this.lastMotion = { x: 0, y: 0, z: 0, time: 0 };
    this.lastMotionJoltAt = 0;
    this.abortController = 'AbortController' in window ? new AbortController() : null;
    this.eventOptions = this.abortController ? { signal: this.abortController.signal } : false;

    if (!this.canvas || !this.context) return;
    this.setup();
  }

  StickerFall.prototype.setup = function () {
    var self = this;
    var pool = [];
    var uniquePool = [];
    var seenSources = {};
    var promises = [];

    this.section.querySelectorAll('[data-ohc-sticker-source]').forEach(function (source) {
      var weight = clamp(Number(source.dataset.weight || 1), 1, 4);
      var index = promises.length;
      var sourceUrl = source.dataset.src;
      var sourceKey = source.dataset.name || sourceUrl;
      if (self.stickerVersion) {
        sourceUrl += (sourceUrl.indexOf('?') === -1 ? '?' : '&') + 'v=' + encodeURIComponent(self.stickerVersion);
      }
      promises.push(loadImage(sourceUrl));
      if (!seenSources[sourceKey]) {
        uniquePool.push(index);
        seenSources[sourceKey] = true;
      }
      for (var i = 0; i < weight; i += 1) pool.push(index);
    });

    if (!promises.length) return;

    this.resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(function () {
      self.resize();
    }) : null;
    if (this.resizeObserver) this.resizeObserver.observe(this.section);
    else window.addEventListener('resize', function () { self.resize(); }, this.eventOptions);

    if ('IntersectionObserver' in window && (!reducedMotion || this.chaosMode)) {
      this.intersectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (self.trigger === 'on_view') {
            self.inView = entry.isIntersecting;
            if (self.inView) self.startLoop();
            else if (!self.dragged && performance.now() >= self.interactionUntil) self.stopLoop();
          } else if (entry.isIntersecting) {
            self.startLoop();
          }
        });
      }, { threshold: 0.01 });
      this.intersectionObserver.observe(this.section);
    } else if (this.trigger === 'on_view') {
      this.inView = true;
    }

    this.bindPointer();
    if (this.shakeButton) {
      this.shakeButton.addEventListener('click', function () {
        self.jolt(1.25);
        self.enableMotion();
      }, this.eventOptions);
    }

    Promise.all(promises).then(function (images) {
      if (self.destroyed) return;
      self.images = images;
      self.pool = pool.filter(function (index) { return images[index]; });
      self.uniquePool = uniquePool.filter(function (index) { return images[index]; });
      if (!self.pool.length) return;
      self.resize();
      self.resetBodies();
      if (reducedMotion && !self.chaosMode) self.drawStaticPile();
      else self.startLoop();
    });
  };

  StickerFall.prototype.resize = function () {
    var self = this;
    var rect = this.section.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var previousWidth = this.width;
    var previousHeight = this.height;
    var previousFloor = this.floorY;
    var dragGeometryChanged = Boolean(previousWidth) && (
      Math.abs(rect.width - previousWidth) > 1 ||
      Math.abs(rect.height - previousHeight) > 4
    );
    if (this.dragged && dragGeometryChanged) this.finishDrag(null, true, true);
    this.width = Math.round(rect.width);
    this.height = Math.round(rect.height);
    var nextViewportTier = viewportTier(this.width);
    var viewportTierChanged = Boolean(this.viewportTier) && this.viewportTier !== nextViewportTier;
    this.viewportTier = nextViewportTier;
    var dprLimit = nextViewportTier === 'wide' ? 1.5 : nextViewportTier === 'tablet' ? 1.75 : 2;
    this.dpr = Math.min(window.devicePixelRatio || 1, dprLimit);
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.canvas.style.width = this.width + 'px';
    this.canvas.style.height = this.height + 'px';
    this.context.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    var previousObstacles = this.obstacles.map(function (obstacle) {
      return { left: obstacle.left, right: obstacle.right, top: obstacle.top, bottom: obstacle.bottom };
    });
    this.measureObstacles();
    var obstaclesMoved = previousObstacles.length > 0 && (
      previousObstacles.length !== this.obstacles.length ||
      this.obstacles.some(function (obstacle, index) {
        var previous = previousObstacles[index];
        return !previous || Math.max(
          Math.abs(obstacle.left - previous.left),
          Math.abs(obstacle.right - previous.right),
          Math.abs(obstacle.top - previous.top),
          Math.abs(obstacle.bottom - previous.bottom)
        ) > 2.5;
      })
    );
    var floor = this.floorY || rect.height - 12;
    var widthRatio = previousWidth ? this.width / previousWidth : 1;
    var nextSizeRange = stickerSizeRange(this.width);
    var nextRadiusRatio = stickerRadiusRatio(this.width);
    var bodiesResized = false;
    var geometryChanged = Boolean(previousWidth) && (
      Math.abs(this.width - previousWidth) > 1 ||
      Math.abs(this.height - previousHeight) > 2 ||
      (previousFloor && Math.abs(floor - previousFloor) > 2.5) ||
      obstaclesMoved
    );

    this.bodies.forEach(function (body) {
      var bodyResized = false;
      if (previousWidth) body.x *= widthRatio;
      if (typeof body.sizeRatio === 'number') {
        var nextSize = nextSizeRange.min + (nextSizeRange.max - nextSizeRange.min) * body.sizeRatio;
        if (Math.abs(nextSize - body.size) > 0.5) {
          body.size = nextSize;
          body.radius = nextSize * nextRadiusRatio;
          bodyResized = true;
          bodiesResized = true;
        }
      }
      body.x = clamp(body.x, body.radius, Math.max(body.radius, rect.width - body.radius));
      if (body.y + body.radius > floor) body.y = floor - body.radius;
      if (bodyResized || geometryChanged) {
        body.sleeping = false;
        body.restFrames = 0;
        body.contacts = 0;
        body.supportType = 'none';
        body.supportsBody = false;
      }
      body.restX = body.x;
      body.restY = body.y;
    });

    if (viewportTierChanged && this.bodies.length && this.pool && this.pool.length) {
      this.reconcileBodiesForTier(nextViewportTier);
      return;
    }

    if (reducedMotion && !this.chaosMode && this.bodies.length) {
      this.layoutStaticPile();
      this.drawStaticPile();
    } else if (bodiesResized || geometryChanged) {
      self.section.dataset.ohcStickerState = 'falling';
      self.startLoop();
    }
  };

  StickerFall.prototype.measureObstacles = function () {
    var heroRect = this.section.getBoundingClientRect();
    this.obstacles = Array.prototype.map.call(this.section.querySelectorAll('.ohc-hero__product'), function (product) {
      var style = product.dataset.ohcPackStyle || 'transparent';
      var target = style === 'framed'
        ? product.querySelector('.ohc-hero__product-stage')
        : product.querySelector('img, .ohc-hero__sample-pack, .ohc-hero__v3-pack, .ohc-hero__mock');
      var rect = (target || product).getBoundingClientRect();
      var sideInset = style === 'framed' ? 0.035 : 0.06;
      var verticalInset = style === 'framed' ? 0.01 : 0.025;
      return {
        left: rect.left - heroRect.left + rect.width * sideInset,
        right: rect.right - heroRect.left - rect.width * sideInset,
        top: rect.top - heroRect.top + rect.height * verticalInset,
        bottom: rect.bottom - heroRect.top - rect.height * verticalInset,
        kind: 'package'
      };
    }).filter(function (rect) {
      return rect.right > rect.left && rect.bottom > rect.top;
    });
    this.floorY = this.obstacles.length ? Math.max.apply(Math, this.obstacles.map(function (rect) { return rect.bottom; })) : this.height - 12;
  };

  StickerFall.prototype.bodyCountForTier = function (tier) {
    var mobile = tier === 'mobile';
    var tablet = tier === 'tablet';
    var wide = tier === 'wide';
    var requested = Number(mobile
      ? this.section.dataset.ohcCountMobile
      : tablet
        ? this.section.dataset.ohcCountTablet
        : wide
          ? this.section.dataset.ohcCountWide
          : this.section.dataset.ohcCountDesktop);
    var fallbackCount = mobile ? 33 : tablet ? 53 : wide ? 84 : 39;
    var maximumCount = mobile ? 36 : tablet ? 96 : wide ? 224 : 96;
    return clamp(Math.max(requested || fallbackCount, (this.uniquePool || []).length), 8, maximumCount);
  };

  StickerFall.prototype.createBody = function (imageIndex, sizeRange, radiusRatio, order, verticalStagger, spawnAt) {
    var sizeRatio = Math.random();
    var size = sizeRange.min + (sizeRange.max - sizeRange.min) * sizeRatio;
    var radius = size * radiusRatio;
    var body = {
      imageIndex: imageIndex,
      image: this.images[imageIndex],
      sizeRatio: sizeRatio,
      size: size,
      radius: radius,
      x: random(radius + 8, Math.max(radius + 9, this.width - radius - 8)),
      y: -random(50, 420) - order * verticalStagger,
      vx: random(-0.45, 0.45),
      vy: random(-0.2, 0.2),
      angle: random(-Math.PI, Math.PI),
      // A short, naturally damped turn as the sticker falls.
      spin: random(-0.025, 0.025),
      contacts: 0,
      restFrames: 0,
      restX: 0,
      restY: 0,
      sleeping: false,
      supportType: 'none',
      supportsBody: false,
      wobblePhase: random(0, Math.PI * 2),
      wobbleSpeed: random(0.74, 1.06),
      wobbleAmount: random(0.024, 0.048),
      airSwayPhase: random(0, Math.PI * 2),
      airSwaySpeed: random(1.35, 2.15),
      airSwayAmount: random(0.018, 0.052),
      rockStartedAt: -100,
      rockKick: 0,
      rockDirection: 1,
      rockFrequency: random(8.4, 11.6),
      chaosAngle: 0,
      chaosHasLanded: false,
      chaosGroundElapsed: 0,
      chaosDirection: Math.random() < 0.5 ? -1 : 1,
      chaosRampDuration: random(5.2, 7.4),
      chaosMaxSpin: random(28, 40),
      active: reducedMotion && !this.chaosMode,
      spawnAt: spawnAt
    };
    body.restX = body.x;
    body.restY = body.y;
    return body;
  };

  StickerFall.prototype.reconcileBodiesForTier = function (tier) {
    if (this.dragged) this.finishDrag(null, true, true);
    var targetCount = this.bodyCountForTier(tier);
    var currentCount = this.bodies.length;
    var tablet = tier === 'tablet';
    var wide = tier === 'wide';
    var sizeRange = stickerSizeRange(this.width);
    var radiusRatio = stickerRadiusRatio(this.width);
    var spawnInterval = wide ? 36 : tablet ? 64 : targetCount > 64 ? 56 : 82;
    var verticalStagger = wide ? 4 : tablet ? 9 : targetCount > 64 ? 11 : 18;

    if (targetCount < currentCount) {
      var removeCount = currentCount - targetCount;
      var imageCounts = new Map();
      this.bodies.forEach(function (body) {
        imageCounts.set(body.imageIndex, (imageCounts.get(body.imageIndex) || 0) + 1);
      });
      var candidates = this.bodies.slice().sort(function (first, second) {
        if (first.active !== second.active) return first.active ? 1 : -1;
        return first.y - second.y;
      });
      var removed = new Set();
      candidates.forEach(function (body) {
        if (!removeCount || (imageCounts.get(body.imageIndex) || 0) <= 1) return;
        removed.add(body);
        imageCounts.set(body.imageIndex, imageCounts.get(body.imageIndex) - 1);
        removeCount -= 1;
      });
      // bodyCountForTier never asks for fewer bodies than unique stickers, so
      // this is only a defensive fallback for malformed or edited source data.
      candidates.forEach(function (body) {
        if (!removeCount || removed.has(body)) return;
        removed.add(body);
        removeCount -= 1;
      });
      this.bodies = this.bodies.filter(function (body) { return !removed.has(body); });
    } else if (targetCount > currentCount) {
      var elapsed = Math.max(0, performance.now() - this.startedAt);
      var presentImages = new Set(this.bodies.map(function (body) { return body.imageIndex; }));
      var missingUnique = (this.uniquePool || []).filter(function (imageIndex) {
        return !presentImages.has(imageIndex);
      });
      var latestSpawnAt = this.bodies.reduce(function (latest, body) {
        return Math.max(latest, body.spawnAt || 0);
      }, 0);
      var firstAddedSpawnAt = Math.max(elapsed + 120, latestSpawnAt + spawnInterval, 180);
      for (var index = currentCount; index < targetCount; index += 1) {
        var addedIndex = index - currentCount;
        var imageIndex = missingUnique.length
          ? missingUnique.shift()
          : this.pool[index % this.pool.length];
        this.bodies.push(this.createBody(
          imageIndex,
          sizeRange,
          radiusRatio,
          addedIndex,
          verticalStagger,
          firstAddedSpawnAt + addedIndex * spawnInterval
        ));
      }
    }

    this.bodies.forEach(function (body) {
      body.sleeping = false;
      body.restFrames = 0;
      body.contacts = 0;
      body.supportType = 'none';
      body.supportsBody = false;
      body.restX = body.x;
      body.restY = body.y;
    });
    this.hasStartedFalling = this.bodies.some(function (body) { return body.active; });
    this.lastFrame = 0;
    this.lastSettledDrawAt = 0;
    this.section.dataset.ohcStickerState = reducedMotion && !this.chaosMode ? 'static' : 'falling';

    if (reducedMotion && !this.chaosMode) {
      this.layoutStaticPile();
      this.drawStaticPile();
    } else {
      this.startLoop();
    }
  };

  StickerFall.prototype.resetBodies = function () {
    if (this.dragged) this.finishDrag(null, true, true);
    var tier = viewportTier(this.width);
    var tablet = tier === 'tablet';
    var wide = tier === 'wide';
    var uniquePool = (this.uniquePool || []).slice();
    for (var shuffleIndex = uniquePool.length - 1; shuffleIndex > 0; shuffleIndex -= 1) {
      var swapIndex = Math.floor(Math.random() * (shuffleIndex + 1));
      var swapValue = uniquePool[shuffleIndex];
      uniquePool[shuffleIndex] = uniquePool[swapIndex];
      uniquePool[swapIndex] = swapValue;
    }
    var count = this.bodyCountForTier(tier);
    var sizeRange = stickerSizeRange(this.width);
    var radiusRatio = stickerRadiusRatio(this.width);
    var spawnInterval = wide ? 36 : tablet ? 64 : count > 64 ? 56 : 82;
    var verticalStagger = wide ? 4 : tablet ? 9 : count > 64 ? 11 : 18;

    this.bodies = [];
    this.hasStartedFalling = false;
    for (var i = 0; i < count; i += 1) {
      var imageIndex = i < uniquePool.length
        ? uniquePool[i]
        : this.pool[(i - uniquePool.length) % this.pool.length];
      this.bodies.push(this.createBody(imageIndex, sizeRange, radiusRatio, i, verticalStagger, 180 + i * spawnInterval));
    }
    this.startedAt = performance.now() + this.delay;
    this.lastFrame = 0;
    this.lastDrawAt = 0;
    this.lastSettledDrawAt = 0;
    this.section.dataset.ohcStickerState = reducedMotion && !this.chaosMode ? 'static' : 'waiting';

    if (reducedMotion && !this.chaosMode) this.layoutStaticPile();
  };

  StickerFall.prototype.layoutStaticPile = function () {
    var self = this;
    var range = stickerSizeRange(this.width);
    var collisionDiameter = range.max * stickerRadiusRatio(this.width) * 2;
    var columnWidth = Math.max(48, collisionDiameter * 0.9);
    var columns = Math.max(4, Math.floor(this.width / columnWidth));
    var rowHeight = Math.max(28, collisionDiameter * 0.55);
    this.bodies.forEach(function (body, index) {
      var row = Math.floor(index / columns);
      var column = index % columns;
      body.x = ((column + 0.5) / columns) * self.width;
      body.y = (self.floorY || self.height - 20) - body.radius - row * rowHeight;
      body.angle = random(-0.28, 0.28);
      body.restX = body.x;
      body.restY = body.y;
    });
  };

  StickerFall.prototype.startLoop = function () {
    var self = this;
    var interactionActive = performance.now() < this.interactionUntil;
    if ((reducedMotion && !this.chaosMode) || this.destroyed || !this.bodies.length || this.raf || (this.trigger === 'on_view' && !this.inView && !this.dragged && !interactionActive)) return;
    this.raf = requestAnimationFrame(function frame(time) {
      self.raf = 0;
      var interactionStillActive = performance.now() < self.interactionUntil;
      if (self.destroyed || (self.trigger === 'on_view' && !self.inView && !self.dragged && !interactionStillActive)) return;
      self.frame(time);
      self.startLoop();
    });
  };

  StickerFall.prototype.stopLoop = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  };

  StickerFall.prototype.frame = function (time) {
    if (!this.width || !this.height) return;
    if (this.section.dataset.ohcStickerState === 'settled' && !this.dragged) {
      if (time - this.lastSettledDrawAt >= 40) {
        this.draw();
        this.lastSettledDrawAt = time;
      }
      return;
    }
    var delta = this.lastFrame ? clamp((time - this.lastFrame) / 16.667, 0.25, 2) : 1;
    this.lastFrame = time;
    var elapsed = time - this.startedAt;

    this.bodies.forEach(function (body) {
      if (!body.active && elapsed >= body.spawnAt) body.active = true;
    });
    if (!this.hasStartedFalling && this.bodies.some(function (body) { return body.active; })) {
      this.hasStartedFalling = true;
      this.section.dataset.ohcStickerState = 'falling';
    }

    var substeps = delta > 1.25 ? 2 : 1;
    for (var i = 0; i < substeps; i += 1) this.step(delta / substeps);
    this.draw();
  };

  StickerFall.prototype.step = function (delta) {
    var self = this;

    // Device orientation sensors are noisy even when a phone appears still.
    // Ease toward a dead-zoned target instead of injecting that noise directly
    // into every resting contact on every frame.
    this.gravityX += (this.gravityTargetX - this.gravityX) * clamp(0.16 * delta, 0, 1);
    if (Math.abs(this.gravityX) < 0.002 && this.gravityTargetX === 0) this.gravityX = 0;

    this.bodies.forEach(function (body) {
      if (!body.active) return;
      body.contacts = 0;
      body.supportsBody = false;
      // Package/floor support can persist while sleeping, but pile support is
      // rebuilt from actual neighbours below on every frame.
      if (!body.sleeping || body.supportType === 'pile') body.supportType = 'none';
    });

    this.bodies.forEach(function (body) {
      if (!body.active) return;
      if (body === self.dragged) {
        body.angle += body.spin * delta;
        body.spin *= Math.pow(0.92, delta);
        if (Math.abs(body.spin) < 0.0007) body.spin = 0;
        return;
      }
      if (body.sleeping) return;
      body.vx += self.gravityX * 0.18 * delta;
      body.vy += 0.27 * self.gravityStrength * delta;
      body.vx *= Math.pow(0.991, delta);
      body.vy *= Math.pow(0.994, delta);
      body.spin *= Math.pow(0.994, delta);
      if (Math.abs(body.spin) < 0.0007) body.spin = 0;
      body.x += body.vx * delta;
      body.y += body.vy * delta;
      body.angle += body.spin * delta;

      self.collideWithBounds(body, delta, false);
      self.collideWithObstacles(body, delta, false);

      if (body.y > self.height + 220 || body.x < -220 || body.x > self.width + 220) {
        body.x = random(body.radius, self.width - body.radius);
        body.y = -random(60, 240);
        body.vx = 0;
        body.vy = 0;
        body.spin = random(-0.025, 0.025);
        body.contacts = 0;
        body.restFrames = 0;
        body.sleeping = false;
        body.supportType = 'none';
        body.supportsBody = false;
        body.rockStartedAt = -100;
        body.rockKick = 0;
      }
    });

    this.collideActiveBodies(delta);

    // A body-to-body correction can push a sticker a fraction outside a wall
    // or into a product. Resolve that in the same simulation step so the next
    // frame cannot visibly snap it back and forth at an edge.
    this.bodies.forEach(function (body) {
      if (!body.active || body === self.dragged || body.sleeping) return;
      self.collideWithBounds(body, delta, true);
      self.collideWithObstacles(body, delta, true);
      self.collideWithBounds(body, delta, true);
    });

    this.bodies.forEach(function (body) {
      if (!body.active || body === self.dragged || body.sleeping) return;
      // Contact solvers can retain velocity while projection keeps a wedged
      // sticker visually still (for example between a pouch and the edge).
      // Sleep on either low velocity or genuinely tiny final displacement.
      var displacementX = body.x - body.restX;
      var displacementY = body.y - body.restY;
      var positionStable = displacementX * displacementX + displacementY * displacementY < 0.03;
      var velocitySlow = body.vx * body.vx + body.vy * body.vy < 0.72 && Math.abs(body.spin) < 0.0045;
      body.restFrames = body.contacts > 0 && (velocitySlow || positionStable) ? body.restFrames + delta : 0;
      body.restX = body.x;
      body.restY = body.y;
      if (body.restFrames >= 28) {
        body.vx = 0;
        body.vy = 0;
        body.spin = 0;
        body.sleeping = true;
      }
    });

    var fullySettled = !this.dragged && this.bodies.length && this.bodies.every(function (body) {
      return body.active && body.sleeping;
    });
    if (fullySettled) this.section.dataset.ohcStickerState = 'settled';
    else if (this.hasStartedFalling) this.section.dataset.ohcStickerState = 'falling';
    if (this.section.hasAttribute('data-ohc-debug')) {
      var activeCount = this.bodies.filter(function (body) { return body.active; }).length;
      var sleepingCount = this.bodies.filter(function (body) { return body.sleeping; }).length;
      var movingBodies = this.bodies.map(function (body, index) {
        if (!body.active || body.sleeping) return null;
        return [index, Number(body.x.toFixed(1)), Number(body.y.toFixed(1)), Number(body.vx.toFixed(2)), Number(body.vy.toFixed(2)), Number(body.spin.toFixed(3)), body.contacts, Number(body.restFrames.toFixed(1)), body.supportType];
      }).filter(Boolean);
      this.section.dataset.ohcDebugPhysics = JSON.stringify([activeCount, sleepingCount, movingBodies]);
    }
  };

  StickerFall.prototype.collideActiveBodies = function (delta) {
    var self = this;

    // Preserve the original collision order for phones and regular desktops.
    // Denser tablet and wide-screen scenes use a spatial grid so collision
    // checks remain responsive while the pile is moving.
    var tier = viewportTier(this.width);
    if (tier !== 'wide' && tier !== 'tablet') {
      for (var i = 0; i < this.bodies.length; i += 1) {
        var first = this.bodies[i];
        if (!first.active) continue;
        for (var j = i + 1; j < this.bodies.length; j += 1) {
          var second = this.bodies[j];
          if (second.active) this.collideBodies(first, second, delta);
        }
      }
      return;
    }

    var sizeRange = stickerSizeRange(this.width);
    var cellSize = Math.max(72, sizeRange.max * stickerRadiusRatio(this.width) * 2 + 2);
    var cells = Object.create(null);

    this.bodies.forEach(function (body) {
      if (!body.active) return;
      var cellX = Math.floor(body.x / cellSize);
      var cellY = Math.floor(body.y / cellSize);

      for (var offsetX = -1; offsetX <= 1; offsetX += 1) {
        for (var offsetY = -1; offsetY <= 1; offsetY += 1) {
          var neighbour = cells[(cellX + offsetX) + ':' + (cellY + offsetY)];
          if (!neighbour) continue;
          neighbour.forEach(function (other) {
            self.collideBodies(other, body, delta);
          });
        }
      }

      var key = cellX + ':' + cellY;
      if (!cells[key]) cells[key] = [];
      cells[key].push(body);
    });
  };

  StickerFall.prototype.collideWithBounds = function (body, delta, correctionOnly) {
    var floor = this.floorY || this.height - 12;
    var wallDamping = Math.pow(0.9, delta);
    var contactDamping = Math.pow(0.84, delta);
    var spinDamping = Math.pow(0.72, delta);

    if (body.x - body.radius < 0) {
      body.x = body.radius;
      body.contacts += 1;
      if (body.vx < 0) {
        var leftImpact = -body.vx;
        if (!correctionOnly && leftImpact > 0.9) kickVisualRock(body, leftImpact * 0.012, 1);
        body.vx = !correctionOnly && leftImpact > 1.15 ? leftImpact * 0.24 : 0;
      }
      body.vy *= wallDamping;
      body.spin *= spinDamping;
    } else if (body.x + body.radius > this.width) {
      body.x = this.width - body.radius;
      body.contacts += 1;
      if (body.vx > 0) {
        var rightImpact = body.vx;
        if (!correctionOnly && rightImpact > 0.9) kickVisualRock(body, rightImpact * 0.012, -1);
        body.vx = !correctionOnly && rightImpact > 1.15 ? -rightImpact * 0.24 : 0;
      }
      body.vy *= wallDamping;
      body.spin *= spinDamping;
    }

    if (body.y + body.radius > floor) {
      body.y = floor - body.radius;
      body.contacts += 1;
      if (body.vy > 0) {
        var floorImpact = body.vy;
        if (!correctionOnly && floorImpact > 0.72) {
          kickVisualRock(body, floorImpact * 0.009 + Math.abs(body.vx) * 0.003, body.vx < 0 ? -1 : 1);
        }
        body.vy = !correctionOnly && floorImpact > 1.15 ? -floorImpact * 0.13 : 0;
      }
      body.vx *= contactDamping;
      body.spin *= spinDamping;
      if (body.supportType !== 'package') body.supportType = 'floor';
    }
  };

  StickerFall.prototype.collideWithObstacles = function (body, delta, correctionOnly) {
    this.obstacles.forEach(function (rect) {
      var left = rect.left - body.radius;
      var right = rect.right + body.radius;
      var top = rect.top - body.radius;
      var bottom = rect.bottom + body.radius;
      if (body.x <= left || body.x >= right || body.y <= top || body.y >= bottom) return;

      var distances = [body.x - left, right - body.x, body.y - top, bottom - body.y];
      var nearest = Math.min.apply(Math, distances);
      var side = distances.indexOf(nearest);
      body.contacts += 1;

      if (side === 0) {
        body.x = left;
        if (body.vx > 0) {
          var leftImpact = body.vx;
          body.vx = !correctionOnly && leftImpact > 1.05 ? -leftImpact * 0.18 : 0;
        }
      } else if (side === 1) {
        body.x = right;
        if (body.vx < 0) {
          var rightImpact = -body.vx;
          body.vx = !correctionOnly && rightImpact > 1.05 ? rightImpact * 0.18 : 0;
        }
      } else if (side === 2) {
        body.y = top;
        if (body.vy > 0) {
          var topImpact = body.vy;
          if (!correctionOnly && topImpact > 0.68) {
            kickVisualRock(body, topImpact * 0.01 + Math.abs(body.vx) * 0.0035, body.vx < 0 ? -1 : 1);
          }
          body.vy = !correctionOnly && topImpact > 1.05 ? -topImpact * 0.12 : 0;
        }
        body.vx *= 0.88;
        body.supportType = 'package';
      } else {
        body.y = bottom;
        if (body.vy < 0) {
          var bottomImpact = -body.vy;
          body.vy = !correctionOnly && bottomImpact > 1.05 ? bottomImpact * 0.16 : 0;
        }
      }

      body.vx *= Math.pow(0.84, delta);
      body.spin *= Math.pow(0.72, delta);
    });
  };

  StickerFall.prototype.collideBodies = function (first, second, delta) {
    var dx = second.x - first.x;
    var dy = second.y - first.y;
    var minimum = first.radius + second.radius;
    var distanceSquared = dx * dx + dy * dy;
    var supportDistance = minimum + 1.75;
    if (distanceSquared >= supportDistance * supportDistance) return;

    var distance = Math.sqrt(distanceSquared) || 0.001;
    var nx = dx / distance;
    var ny = dy / distance;

    // Remember which sticker is balanced on another sticker. This state is
    // rebuilt with a tiny contact tolerance, so removing the previous top
    // sticker immediately lets the newly exposed one rock again.
    if (ny > 0.35) {
      if (first.supportType === 'none') first.supportType = 'pile';
      second.supportsBody = true;
    } else if (ny < -0.35) {
      if (second.supportType === 'none') second.supportType = 'pile';
      first.supportsBody = true;
    }

    // Near-touching sleeping bodies need the support relation above, but only
    // a real overlap should enter the position and impulse solver.
    if (distanceSquared >= minimum * minimum) return;

    var overlap = minimum - distance;
    var relativeX = second.vx - first.vx;
    var relativeY = second.vy - first.vy;
    var speed = relativeX * nx + relativeY * ny;
    var impact = Math.max(0, -speed);

    first.contacts += 1;
    second.contacts += 1;

    // A real hit wakes a resting sticker; tiny pile overlaps do not.
    if (impact > 1.4) {
      first.sleeping = false;
      first.restFrames = 0;
      first.supportType = 'none';
      first.supportsBody = false;
      second.sleeping = false;
      second.restFrames = 0;
      second.supportType = 'none';
      second.supportsBody = false;
    }

    var firstFixed = first === this.dragged || first.sleeping;
    var secondFixed = second === this.dragged || second.sleeping;
    if (firstFixed && secondFixed) return;
    var split = firstFixed ? 0 : (secondFixed ? 1 : 0.5);

    first.x -= nx * overlap * split;
    first.y -= ny * overlap * split;
    second.x += nx * overlap * (1 - split);
    second.y += ny * overlap * (1 - split);

    if (!firstFixed) {
      first.vx *= Math.pow(0.84, delta);
      first.spin *= Math.pow(0.72, delta);
    }
    if (!secondFixed) {
      second.vx *= Math.pow(0.84, delta);
      second.spin *= Math.pow(0.72, delta);
    }

    if (speed >= 0) return;

    var movableBodies = (firstFixed ? 0 : 1) + (secondFixed ? 0 : 1);
    var impulse = -(1.12 * speed) / Math.max(1, movableBodies);
    if (!firstFixed) {
      first.vx -= impulse * nx;
      first.vy -= impulse * ny;
    }
    if (!secondFixed) {
      second.vx += impulse * nx;
      second.vy += impulse * ny;
    }

    // Only an angled, meaningful impact adds rotation. Persistent overlap in a
    // settled pile therefore cannot keep feeding spin back into the stickers.
    if (impact > 0.55) {
      var tangentSpeed = relativeX * -ny + relativeY * nx;
      var spinKick = clamp(tangentSpeed * 0.0025, -0.025, 0.025);
      if (!firstFixed) first.spin = clamp(first.spin - spinKick, -0.09, 0.09);
      if (!secondFixed) second.spin = clamp(second.spin + spinKick, -0.09, 0.09);
      var rockAmount = impact * 0.007 + Math.abs(tangentSpeed) * 0.0025;
      var rockDirection = tangentSpeed < 0 ? -1 : 1;
      kickVisualRock(first, rockAmount, rockDirection);
      kickVisualRock(second, rockAmount, -rockDirection);
    }
  };

  StickerFall.prototype.draw = function () {
    var context = this.context;
    var now = performance.now() / 1000;
    var drawDelta = this.lastDrawAt ? clamp(now - this.lastDrawAt, 0, 0.05) : 0;
    this.lastDrawAt = now;
    var chaosMode = this.chaosMode;
    var landingThreshold = this.height * 0.42;
    var bodiesToDraw = this.dragged
      ? this.bodies.filter(function (body) { return body !== this.dragged; }, this).concat([this.dragged])
      : this.bodies;
    context.clearRect(0, 0, this.width, this.height);
    bodiesToDraw.forEach(function (body) {
      if (!body.active || !body.image) return;
      var visualAngle = body.angle;
      var balanceAngle = 0;
      if (chaosMode) {
        var landed = body.sleeping || body.supportType === 'package' || body.supportType === 'floor' || (body.contacts > 0 && body.y > landingThreshold);
        if (!body.chaosHasLanded && landed) {
          body.chaosHasLanded = true;
          body.chaosGroundElapsed = 0;
        }
        if (body.chaosHasLanded) {
          body.chaosGroundElapsed += drawDelta;
          var ramp = clamp(body.chaosGroundElapsed / body.chaosRampDuration, 0, 1);
          var chaosSpin = body.chaosDirection * body.chaosMaxSpin * ramp * ramp;
          body.chaosAngle += chaosSpin * drawDelta;
          visualAngle += body.chaosAngle;
        }
      }
      if (!chaosMode && !body.sleeping && body.contacts === 0) {
        visualAngle += Math.sin(now * body.airSwaySpeed + body.airSwayPhase) * body.airSwayAmount;
      }

      // Meaningful impacts create a short damped rock. It is deliberately kept
      // out of body.angle/spin so contacts can feel lively without reintroducing
      // endless physical rotation or edge jitter.
      if (!chaosMode) {
        var rockAge = now - body.rockStartedAt;
        if (rockAge >= 0 && rockAge < 2.8) {
          visualAngle += Math.sin(rockAge * body.rockFrequency) * body.rockKick * body.rockDirection * Math.exp(-2.05 * rockAge);
        }
      }

      // Only the top sticker in a stack or on a pouch keeps a tiny slow balance
      // rock. Stickers carrying another sticker remain visually still so the
      // whole pile never appears to vibrate as one block.
      var isPrecarious = !chaosMode && body.sleeping && !body.supportsBody && (body.supportType === 'package' || body.supportType === 'pile');
      if (isPrecarious) {
        var supportFactor = body.supportType === 'package' ? 1 : 0.72;
        balanceAngle += Math.sin(now * body.wobbleSpeed + body.wobblePhase) * body.wobbleAmount * supportFactor;
        balanceAngle += Math.sin(now * body.wobbleSpeed * 0.43 + body.wobblePhase * 1.7) * body.wobbleAmount * supportFactor * 0.24;
      }
      context.save();
      context.translate(body.x, body.y);
      context.rotate(visualAngle);
      // Rock a balanced sticker around its lower contact point instead of its
      // centre. The tiny lateral shift is what makes it feel perched rather
      // than merely rotating in place.
      if (balanceAngle) {
        var pivotOffset = body.size * 0.31;
        context.translate(0, pivotOffset);
        context.rotate(balanceAngle);
        context.translate(0, -pivotOffset);
      }
      context.drawImage(body.image, -body.size / 2, -body.size / 2, body.size, body.size);
      context.restore();
    });
  };

  StickerFall.prototype.drawStaticPile = function () {
    this.draw();
  };

  StickerFall.prototype.pointerPosition = function (event) {
    var rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  StickerFall.prototype.findBodyAtPoint = function (point) {
    for (var i = this.bodies.length - 1; i >= 0; i -= 1) {
      var body = this.bodies[i];
      if (!body.active || !body.image) continue;
      var dx = point.x - body.x;
      var dy = point.y - body.y;
      // Collision circles are intentionally compact so piles look layered.
      // Dragging needs a separate, larger hit area that covers the visible
      // white-edged sticker rather than only its small physical centre.
      var hitRadius = Math.max(body.radius + 8, body.size * 0.56);
      if (dx * dx + dy * dy <= hitRadius * hitRadius) return body;
    }
    return null;
  };

  StickerFall.prototype.wakeNearbyBodies = function (source) {
    this.bodies.forEach(function (body) {
      if (!body.active) return;
      var dx = body.x - source.x;
      var dy = body.y - source.y;
      var wakeDistance = body === source ? 1 : body.radius + source.radius + 8;
      if (body !== source && dx * dx + dy * dy > wakeDistance * wakeDistance) return;
      body.sleeping = false;
      body.restFrames = 0;
      body.contacts = 0;
      body.supportType = 'none';
      body.supportsBody = false;
      body.restX = body.x;
      body.restY = body.y;
    });
  };

  StickerFall.prototype.finishDrag = function (event, forceCancelled, suppressRestart) {
    if (!this.dragged || !this.dragPoint) return false;
    if (event && event.pointerId != null && event.pointerId !== this.dragPoint.pointerId) return false;

    var body = this.dragged;
    var releasePoint = this.dragPoint;
    var pointerId = releasePoint.pointerId;
    var eventType = event && event.type ? event.type : 'pointercancel';
    var cancelled = Boolean(forceCancelled || eventType === 'pointercancel' || eventType === 'lostpointercapture' || eventType === 'blur');

    // Clear the lock before releasing capture. Some browsers dispatch
    // lostpointercapture synchronously, and it must be safe to enter twice.
    this.dragged = null;
    this.dragPoint = null;
    if (eventType !== 'lostpointercapture' && this.canvas.hasPointerCapture && this.canvas.hasPointerCapture(pointerId)) {
      try {
        this.canvas.releasePointerCapture(pointerId);
      } catch (error) {}
    }
    if (suppressRestart) {
      // A resize can cancel a live drag while keeping this body in the pile.
      // Do not retain the last pointer velocity or it can shoot away as soon
      // as the resized simulation resumes.
      body.vx = 0;
      body.vy = 0;
      body.spin *= 0.2;
      body.restX = body.x;
      body.restY = body.y;
      return true;
    }

    var staleFor = Math.max(0, performance.now() - releasePoint.time);
    var releaseFactor = cancelled ? 0.12 : (staleFor <= 80 ? 1 : clamp(1 - (staleFor - 80) / 220, 0.18, 1));
    body.vx = clamp(releasePoint.vx * releaseFactor, -14, 14);
    body.vy = clamp(releasePoint.vy * releaseFactor, -14, 14);
    var releaseSpeed = Math.sqrt(body.vx * body.vx + body.vy * body.vy);
    if (!cancelled && releaseSpeed < 0.55) {
      body.vx += random(-0.35, 0.35);
      body.vy = -0.65;
      releaseSpeed = Math.sqrt(body.vx * body.vx + body.vy * body.vy);
    }
    var releaseDirection = Math.abs(body.vx) > 0.2 ? (body.vx < 0 ? -1 : 1) : (Math.random() < 0.5 ? -1 : 1);
    body.spin = clamp(
      body.spin * 0.45 + body.vx / Math.max(body.size, 1) * 0.32 + random(-0.003, 0.003),
      -0.075,
      0.075
    );
    kickVisualRock(body, clamp(0.016 + releaseSpeed * 0.003, 0.016, 0.06), releaseDirection);
    this.wakeNearbyBodies(body);
    body.restX = body.x;
    body.restY = body.y;
    this.section.dataset.ohcStickerState = 'falling';
    this.lastFrame = 0;
    this.interactionUntil = performance.now() + 4000;
    this.startLoop();
    return true;
  };

  StickerFall.prototype.bindPointer = function () {
    var self = this;
    this.canvas.addEventListener('pointerdown', function (event) {
      if ((reducedMotion && !self.chaosMode) || self.dragged) return;
      var point = self.pointerPosition(event);
      var body = self.findBodyAtPoint(point);
      if (!body) return;
      var now = performance.now();
      self.dragged = body;
      self.dragPoint = {
        x: point.x,
        y: point.y,
        time: now,
        pointerId: event.pointerId,
        offsetX: body.x - point.x,
        offsetY: body.y - point.y,
        vx: 0,
        vy: 0
      };
      self.wakeNearbyBodies(body);
      body.vx = 0;
      body.vy = 0;
      body.spin *= 0.35;
      self.interactionUntil = now + 4000;
      self.section.dataset.ohcStickerState = 'falling';
      self.lastSettledDrawAt = 0;
      self.startLoop();
      if (self.canvas.setPointerCapture) {
        try {
          self.canvas.setPointerCapture(event.pointerId);
        } catch (error) {}
      }
      event.preventDefault();
    }, this.eventOptions);

    window.addEventListener('pointermove', function (event) {
      if (!self.dragged || !self.dragPoint || event.pointerId !== self.dragPoint.pointerId) return;
      var point = self.pointerPosition(event);
      var now = performance.now();
      var elapsed = Math.max(8, now - self.dragPoint.time);
      var deltaX = point.x - self.dragPoint.x;
      var deltaY = point.y - self.dragPoint.y;
      var rawVx = clamp(deltaX / elapsed * 16.667, -18, 18);
      var rawVy = clamp(deltaY / elapsed * 16.667, -18, 18);
      var nextVx = self.dragPoint.vx * 0.58 + rawVx * 0.42;
      var nextVy = self.dragPoint.vy * 0.58 + rawVy * 0.42;
      self.dragged.vx = nextVx;
      self.dragged.vy = nextVy;
      self.dragged.angle += clamp(deltaX / Math.max(self.dragged.size, 1) * 0.22, -0.09, 0.09);
      self.dragged.spin = clamp(
        self.dragged.spin * 0.55 + rawVx / Math.max(self.dragged.size, 1) * 0.22,
        -0.075,
        0.075
      );
      self.dragged.x = clamp(point.x + self.dragPoint.offsetX, self.dragged.radius, self.width - self.dragged.radius);
      self.dragged.y = clamp(point.y + self.dragPoint.offsetY, -self.dragged.radius, self.height - self.dragged.radius);
      self.dragPoint = {
        x: point.x,
        y: point.y,
        time: now,
        pointerId: self.dragPoint.pointerId,
        offsetX: self.dragPoint.offsetX,
        offsetY: self.dragPoint.offsetY,
        vx: nextVx,
        vy: nextVy
      };
      self.interactionUntil = now + 4000;
      self.section.dataset.ohcStickerState = 'falling';
      if (!self.raf) self.draw();
      self.startLoop();
      event.preventDefault();
    }, this.eventOptions);

    function release(event) {
      self.finishDrag(event, event.type !== 'pointerup', false);
    }

    window.addEventListener('pointerup', release, this.eventOptions);
    window.addEventListener('pointercancel', release, this.eventOptions);
    this.canvas.addEventListener('lostpointercapture', release, this.eventOptions);
    window.addEventListener('blur', function () {
      self.finishDrag(null, true, false);
    }, this.eventOptions);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.finishDrag(null, true, false);
    }, this.eventOptions);

    // Keep normal page scrolling when the user starts on empty canvas, but
    // claim the touch gesture when it begins on a fruit so vertical dragging
    // is not converted into a browser scroll and pointercancel.
    var touchOptions = this.abortController
      ? { signal: this.abortController.signal, passive: false }
      : { passive: false };
    this.canvas.addEventListener('touchstart', function (event) {
      if (reducedMotion && !self.chaosMode) return;
      if (self.dragged) {
        event.preventDefault();
        return;
      }
      var touch = event.changedTouches && event.changedTouches[0];
      if (touch && self.findBodyAtPoint(self.pointerPosition(touch))) event.preventDefault();
    }, touchOptions);
    this.canvas.addEventListener('touchmove', function (event) {
      if (self.dragged) event.preventDefault();
    }, touchOptions);
  };

  StickerFall.prototype.jolt = function (power) {
    if (reducedMotion && !this.chaosMode) return;
    var chaosMode = this.chaosMode;
    this.section.dataset.ohcStickerState = 'falling';
    this.bodies.forEach(function (body) {
      if (!body.active) return;
      body.sleeping = false;
      body.restFrames = 0;
      body.contacts = 0;
      body.supportType = 'none';
      body.supportsBody = false;
      if (chaosMode) {
        body.chaosAngle = 0;
        body.chaosHasLanded = false;
        body.chaosGroundElapsed = 0;
      }
      body.vx += random(-5.5, 5.5) * power;
      body.vy -= random(4, 10) * power;
      body.spin = random(-0.055, 0.055);
    });
    this.startLoop();
  };

  StickerFall.prototype.enableMotion = function () {
    var self = this;
    if (this.motionEnabled || this.motionPermissionPending || (reducedMotion && !this.chaosMode)) return;
    this.motionPermissionPending = true;

    function attach() {
      if (self.motionEnabled) return;
      self.motionPermissionPending = false;
      self.motionEnabled = true;
      window.addEventListener('devicemotion', function (event) { self.handleMotion(event); }, self.eventOptions);
      window.addEventListener('deviceorientation', function (event) {
        if (event.gamma == null) return;
        var tilt = clamp(event.gamma / 45, -0.9, 0.9);
        var deadZone = 0.14;
        if (Math.abs(tilt) <= deadZone) {
          self.gravityTargetX = 0;
        } else {
          self.gravityTargetX = Math.sign(tilt) * (Math.abs(tilt) - deadZone) / (1 - deadZone);
        }
      }, self.eventOptions);
    }

    var motion = window.DeviceMotionEvent;
    var orientation = window.DeviceOrientationEvent;
    if (motion && typeof motion.requestPermission === 'function') {
      motion.requestPermission().then(function (state) {
        if (state === 'granted') attach();
        else self.motionPermissionPending = false;
      }).catch(function () { self.motionPermissionPending = false; });
      if (orientation && typeof orientation.requestPermission === 'function') {
        orientation.requestPermission().catch(function () {});
      }
    } else {
      attach();
    }
  };

  StickerFall.prototype.handleMotion = function (event) {
    var acceleration = event.accelerationIncludingGravity || event.acceleration;
    if (!acceleration) return;
    var now = Date.now();
    if (now - this.lastMotion.time < 90) return;
    var score = Math.abs((acceleration.x || 0) - this.lastMotion.x) +
      Math.abs((acceleration.y || 0) - this.lastMotion.y) +
      Math.abs((acceleration.z || 0) - this.lastMotion.z);
    if (score > 22 && now - this.lastMotionJoltAt > 550) {
      this.lastMotionJoltAt = now;
      this.jolt(1);
    }
    this.lastMotion = { x: acceleration.x || 0, y: acceleration.y || 0, z: acceleration.z || 0, time: now };
  };

  StickerFall.prototype.destroy = function () {
    this.destroyed = true;
    this.finishDrag(null, true, true);
    this.stopLoop();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    if (this.abortController) this.abortController.abort();
    this.section.removeAttribute('data-ohc-sticker-ready');
  };

  function init(root) {
    var scope = root || document;
    var sections = [];
    if (scope.matches && scope.matches('[data-ohc-sticker-hero]:not([data-ohc-sticker-ready])')) sections.push(scope);
    scope.querySelectorAll('[data-ohc-sticker-hero]:not([data-ohc-sticker-ready])').forEach(function (section) { sections.push(section); });
    sections.forEach(function (section) {
      section.setAttribute('data-ohc-sticker-ready', 'true');
      var instance = new StickerFall(section);
      instances.set(section, instance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.querySelector('[data-ohc-sticker-hero]') || (event.target.matches && event.target.matches('[data-ohc-sticker-hero]') ? event.target : null);
    var instance = section && instances.get(section);
    if (instance) {
      instance.destroy();
      instances.delete(section);
    }
  });
})();
